import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import * as styles from './TopicUserCollapsibleList.module.scss';
import TopicUserList from './TopicUserList';
import TopicUsersPanelRow from './TopicUsersPanelRow';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';

const MEMBERS_FRAGMENT = gql(`
  fragment TopicUserCollapsibleList_members on User {
    ...TopicUserList_members
  }
`);

const OWNERS_FRAGMENT = gql(`
  fragment TopicUserCollapsibleList_owners on User {
    ...TopicUserList_owners
  }
`);

interface TopicUserCollapsibleListProps {
  badge?: React.ReactNode;
  icon: React.ReactNode;
  members?: DocumentType<typeof MEMBERS_FRAGMENT>[];
  menu?: React.ReactNode;
  name: React.ReactNode;
  owners?: DocumentType<typeof OWNERS_FRAGMENT>[];
}

const TopicUserCollapsibleList = ({
  badge,
  icon,
  members = [],
  menu = null,
  name,
  owners = [],
}: TopicUserCollapsibleListProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const currentUser = useAppSelector((state) => state.session.user);

  const { uniqueMemberCount, isOwner, isMember } = useMemo(
    () => ({
      uniqueMemberCount: _.uniqBy([...members, ...owners], 'id').length,
      isOwner: !!owners.find((owner) => owner.id === currentUser.id),
      isMember: !!members.find((member) => member.id === currentUser.id),
    }),
    [members, owners, currentUser.id],
  );

  return (
    <div>
      <TopicUsersPanelRow
        icon={icon}
        content={
          <Button
            onClick={() => setIsCollapsed((value) => !value)}
            className={styles.buttonContainer}
          >
            <div className={styles.buttonContent}>
              <span className={styles.name}>
                {name} ({uniqueMemberCount})
              </span>
              {isOwner && (
                <div className={styles.memberNote}>You are an owner</div>
              )}
              {isMember && (
                <div className={styles.memberNote}>You are a member</div>
              )}
            </div>
            {badge}
            <Caret inline direction={isCollapsed ? 'down' : 'up'} size={7} />
          </Button>
        }
        menu={menu}
      />
      {!isCollapsed && (
        <div className={styles.users}>
          {owners.length > 0 || members.length > 0 ? (
            <TopicUserList owners={owners} members={members} compact />
          ) : (
            <div className={styles.emptyMessage}>No members yet</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopicUserCollapsibleList;
